// Sherpa-ONNX Voice Service - Fully Offline Speech Recognition
// Uses WebAssembly with VAD (Voice Activity Detection) + ASR (Automatic Speech Recognition)
//
// This module provides:
// 1. Wake word detection (text-based pattern matching on transcribed speech)
// 2. Speech-to-text using Sherpa-ONNX WASM with VAD
// 3. Voice Activity Detection for natural speech segmentation

import type { VoiceIntent, VoiceState, ComponentCategory, ConditionState } from '@/types';

// Path to WASM files (relative to public directory)
const SHERPA_BASE_PATH = '/sherpa';

// Wake word patterns (matched against transcribed text)
// Comprehensive phonetic variations for "Hey Ventura"
// The offline model produces various interpretations of spoken "Hey Ventura"
//
// Phonetic breakdown:
// - "Hey" /heɪ/ → hey, hi, hay, a, ah, aye, ha, he, ai, eh, hei, ey
// - "Ven" /vɛn/ → ven, van, ben, wen, when, vein, vain, evan, even, haven
// - "tura" /tʊrə/ → tura, turer, turo, chura, churra, turah, ture, tora, torer, terra, tera, chra
//
// Common model mishearings observed:
// - "HAVEN TURER" (hey+ven → haven)
// - "A VENTURO"
// - "havenura"
// - "EVEN TURA"
// - "HE VENTURER"
// - "EVAN TURA" (sounds like a name)
//
const WAKE_WORD_PATTERNS = [
  // ============================================
  // === STANDARD/CLEAR FORMS ===
  // ============================================
  /\bhey ventura\b/i,
  /\bhi ventura\b/i,
  /\bhello ventura\b/i,
  /\byo ventura\b/i,
  /\bok ventura\b/i,
  /\bhey there ventura\b/i,

  // ============================================
  // === "HEY" VARIATIONS + "VENTURA" VARIANTS ===
  // ============================================
  // All common "hey" sounds paired with "ventura" variants
  /\b(hey|hi|hay|a|ah|aye|ha|he|eh|ai|hei|ey|ay)\s*(ventura|venturo|venturer|ventera|ventra|venchura|venchure)\b/i,
  // With "b" instead of "v" (common mishearing)
  /\b(hey|hi|hay|a|ah|aye|ha|he|eh|ai)\s*(bentura|benturo|benturer|bentera|bentra|benchura)\b/i,
  // With "w" instead of "v"
  /\b(hey|hi|hay|a|ah|aye|ha|he)\s*(wentura|wenturo|wenturer|wentera)\b/i,

  // ============================================
  // === "HAVEN" PATTERN (hey+ven → haven) ===
  // ============================================
  // This is VERY common - model merges "hey ven" into "haven"
  /\bhaven\s*(tura|turer|turo|chura|churra|turah|ture|tora|torer|churer|terra|tera|chra|churer|tora|torah)\b/i,
  /\bhaventura\b/i,
  /\bhavenuro\b/i,
  /\bhavenura\b/i,
  /\bhavenchura\b/i,
  /\bhaven\s*ture\b/i,
  /\bhaven\s*chure\b/i,
  /\bhaven\s*tour\w*\b/i,

  // ============================================
  // === "HEAVEN" PATTERN (hey-ven → heaven) ===
  // ============================================
  /\bheaven\s*(tura|turer|turo|chura|turah|ture|terra|tora)\b/i,
  /\bheaventura\b/i,

  // ============================================
  // === "EVEN" PATTERN (hey ven → even) ===
  // ============================================
  /\beven\s*(tura|turer|turo|chura|turah|ture|terra|tora)\b/i,
  /\beventura\b/i,
  /\beventurer\b/i,
  /\bevenchura\b/i,

  // ============================================
  // === "EVAN" PATTERN (sounds like a name) ===
  // ============================================
  /\bevan\s*(tura|turer|turo|chura|turah|ture|terra|tora)\b/i,
  /\bevantura\b/i,

  // ============================================
  // === "HAVAN/HAVON/HAVIN" VARIANTS ===
  // ============================================
  /\bhavan\s*(tura|turer|chura|ture|turo)\b/i,
  /\bhavon\s*(tura|turer|chura|ture|turo)\b/i,
  /\bhavin\s*(tura|turer|chura|ture|turo)\b/i,

  // ============================================
  // === COMPRESSED FORMS (no space) ===
  // ============================================
  /\bheyventura\b/i,
  /\bhiventura\b/i,
  /\baventura\b/i,   // Spanish spelling - very common!
  /\baventuro\b/i,
  /\baventurer\b/i,
  /\boventura\b/i,
  /\biventura\b/i,

  // ============================================
  // === "VAN" SPLIT PATTERN (ven → van) ===
  // ============================================
  /\b(hey|hi|hay|a|ha|he|eh)\s*van\s*(tura|turer|turo|chura|ture|terra)\b/i,
  /\bvan\s*tura\b/i,
  /\bvan\s*turer\b/i,

  // ============================================
  // === "BEN" MISHEARING (v → b) ===
  // ============================================
  /\b(hey|hi|hay|a|ha|he)\s*ben\s*(tura|turer|turo|chura|ture)\b/i,

  // ============================================
  // === "WEN" MISHEARING (v → w) ===
  // ============================================
  /\b(hey|hi|hay|a|ha|he)\s*wen\s*(tura|turer|turo|chura|ture)\b/i,

  // ============================================
  // === SPLIT ACROSS WORDS ===
  // ============================================
  /\bhe\s*ventura\b/i,
  /\bhe\s*venturer\b/i,
  /\bhave\s*entura\b/i,
  /\bhave\s*enturer\b/i,
  /\bhey\s*ven\s*tura\b/i,
  /\bha\s*ven\s*tura\b/i,

  // ============================================
  // === "AVEN" PREFIX (hey → silent 'a') ===
  // ============================================
  /\baven\s*(tura|turer|turo|chura|ture|terra)\b/i,
  /\bave\s*ventura\b/i,
  /\bave\s*venturer\b/i,
  /\baven\s*chura\b/i,

  // ============================================
  // === "VENTURE" WITHOUT FINAL 'A' ===
  // ============================================
  /\b(hey|hi|a|ha|he)\s*venture\b/i,
  /\bhaven\s*ture\b/i,
  /\beven\s*ture\b/i,
  /\bevan\s*ture\b/i,

  // ============================================
  // === JUST THE NAME (hey cut off/missed) ===
  // ============================================
  // Only match if entire utterance
  /^ventura$/i,
  /^venturo$/i,
  /^venturer$/i,
  /^ventera$/i,
  /^bentura$/i,
  /^venchura$/i,
  /^aventura$/i,

  // ============================================
  // === PHONETICALLY SIMILAR PHRASES ===
  // ============================================
  /\ba\s*venture\b/i,
  /\bhey\s*venture\b/i,
  /\bhave\s*an?\s*ture?r?\b/i,
  /\bhaven't\s*ure?r?\b/i,
  /\bhey\s*mentor\w*\b/i,   // mentor sounds similar

  // ============================================
  // === "VEIN/VAIN" MISHEARINGS ===
  // ============================================
  /\b(hey|hi|a|ha)\s*vein\s*(tura|turer|chura)\b/i,
  /\b(hey|hi|a|ha)\s*vain\s*(tura|turer|chura)\b/i,

  // ============================================
  // === UNUSUAL VOWEL COMBINATIONS ===
  // ============================================
  /\b(hey|hi|a|ha|he)\s*(ventura|ventora|ventara|ventyra|vantara|vantora|vanturo)\b/i,
  /\bhaven\s*(tora|tara|tyra|tira)\b/i,

  // ============================================
  // === "WHEN" MISHEARING ===
  // ============================================
  /\b(hey|hi|a)\s*when\s*(tura|turer|chura)\b/i,

  // ============================================
  // === WILDCARD PATTERNS (flexible endings) ===
  // ============================================
  /\bhaven\s+tur\w*\b/i,    // "HAVEN TUR..." any ending
  /\bhaven\s+chur\w*\b/i,   // "HAVEN CHUR..." any ending
  /\beven\s+tur\w*\b/i,     // "EVEN TUR..." any ending
  /\bevan\s+tur\w*\b/i,     // "EVAN TUR..." any ending
  /\bheaven\s+tur\w*\b/i,   // "HEAVEN TUR..." any ending
  /\bhavan\s+tur\w*\b/i,    // "HAVAN TUR..." any ending
  /\baven\s+tur\w*\b/i,     // "AVEN TUR..." any ending

  // ============================================
  // === VERY PERMISSIVE PATTERNS ===
  // ============================================
  // Catch-all for "..ven..tur.." pattern
  /\b\w*ven\s*tur\w*\b/i,
  // Catch-all for "haven/heaven" + any "t" word
  /\b(haven|heaven|havan|even|evan)\s+t\w+\b/i,

  // ============================================
  // === NUMBERS/SOUNDS THAT MIGHT PRECEDE ===
  // ============================================
  /\b(eight|ate|hey|a)\s*ventura\b/i,

  // ============================================
  // === FILLER WORDS BEFORE NAME ===
  // ============================================
  /\b(oh|uh|um|ah)\s*(hey\s*)?(ventura|venturo|venturer)\b/i,
  /\byeah\s*ventura\b/i,
];

// Helper function to test wake word (for debugging)
export function testWakeWord(text: string): boolean {
  return WAKE_WORD_PATTERNS.some(pattern => pattern.test(text));
}

// Follow-up window duration (ms)
const FOLLOW_UP_WINDOW_MS = 5000;

// Sample rate for Sherpa-ONNX
const EXPECTED_SAMPLE_RATE = 16000;

/**
 * Voice service configuration
 */
export interface SherpaVoiceConfig {
  language?: string;
  continuous?: boolean;
  followUpDurationMs?: number;
  onStateChange?: (state: VoiceState) => void;
  onTranscript?: (transcript: string, isFinal: boolean) => void;
  onIntent?: (intent: VoiceIntent) => void;
  onError?: (error: Error) => void;
  onLoadProgress?: (progress: number, message: string) => void;
}

// Global declarations for Sherpa-ONNX WASM types
declare global {
  interface Window {
    Module: SherpaModule;
    createVad?: (module: SherpaModule, config?: VadConfig) => SherpaVad;
    CircularBuffer?: new (capacity: number, module: SherpaModule) => SherpaCircularBuffer;
    OfflineRecognizer?: new (config: OfflineRecognizerConfig, module: SherpaModule) => SherpaOfflineRecognizer;
  }
}

interface SherpaModule {
  locateFile?: (path: string, scriptDirectory: string) => string;
  setStatus?: (status: string) => void;
  onRuntimeInitialized?: () => void;
  _SherpaOnnxFileExists?: (buffer: number) => number;
  lengthBytesUTF8?: (str: string) => number;
  _malloc?: (size: number) => number;
  _free?: (ptr: number) => void;
  stringToUTF8?: (str: string, ptr: number, len: number) => void;
}

interface VadConfig {
  sileroVad?: {
    model: string;
    threshold: number;
    minSilenceDuration: number;
    minSpeechDuration: number;
    maxSpeechDuration: number;
    windowSize: number;
  };
}

interface SherpaVad {
  config: {
    sileroVad: {
      windowSize: number;
    };
  };
  acceptWaveform: (samples: Float32Array) => void;
  isDetected: () => boolean;
  isEmpty: () => boolean;
  front: () => { samples: Float32Array; start: number };
  pop: () => void;
  reset: () => void;
  flush: () => void;
  free: () => void;
}

interface SherpaCircularBuffer {
  push: (samples: Float32Array) => void;
  get: (startIndex: number, n: number) => Float32Array;
  pop: (n: number) => void;
  size: () => number;
  head: () => number;
  reset: () => void;
  free: () => void;
}

interface OfflineRecognizerConfig {
  modelConfig: {
    debug: number;
    tokens: string;
    transducer?: {
      encoder: string;
      decoder: string;
      joiner: string;
    };
  };
}

interface SherpaOfflineRecognizer {
  createStream: () => SherpaOfflineStream;
  decode: (stream: SherpaOfflineStream) => void;
  getResult: (stream: SherpaOfflineStream) => { text: string };
  free: () => void;
}

interface SherpaOfflineStream {
  acceptWaveform: (sampleRate: number, samples: Float32Array) => void;
  free: () => void;
}

/**
 * Sherpa-ONNX Voice Service
 * Provides fully offline wake word detection and speech-to-text using VAD+ASR
 */
export class SherpaVoiceService {
  private config: SherpaVoiceConfig;
  private state: VoiceState = 'idle';

  // Audio processing
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private recorder: ScriptProcessorNode | null = null;
  private recordSampleRate = 48000;

  // Sherpa-ONNX VAD+ASR components
  private vad: SherpaVad | null = null;
  private buffer: SherpaCircularBuffer | null = null;
  private recognizer: SherpaOfflineRecognizer | null = null;
  private isInitialized = false;
  private isLoading = false;

  // State
  private isListeningForCommand = false;
  private currentTranscript = '';
  private speechDetected = false;
  private followUpTimeout: ReturnType<typeof setTimeout> | null = null;
  private explicitlyStopped = false;

  constructor(config: SherpaVoiceConfig = {}) {
    this.config = {
      language: 'en-US',
      continuous: true,
      followUpDurationMs: FOLLOW_UP_WINDOW_MS,
      ...config,
    };
  }

  /**
   * Initialize the voice service - loads WASM module
   */
  async initialize(): Promise<boolean> {
    if (this.isInitialized) return true;
    if (this.isLoading) return false;

    this.isLoading = true;
    this.config.onLoadProgress?.(0, 'Loading offline voice recognition...');

    try {
      await this.loadSherpaWasm();
      this.isInitialized = true;
      this.config.onLoadProgress?.(100, 'Ready');
      console.log('Sherpa-ONNX VAD+ASR voice service initialized successfully');
      return true;
    } catch (error) {
      console.error('Failed to initialize Sherpa-ONNX:', error);
      this.config.onError?.(error instanceof Error ? error : new Error('Failed to load offline voice'));
      return false;
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Load Sherpa-ONNX WASM module with VAD+ASR
   */
  private loadSherpaWasm(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (this.recognizer && this.vad) {
        console.log('Sherpa-ONNX: Already initialized');
        resolve();
        return;
      }

      // Check if scripts are already in DOM
      const existingVadScript = document.querySelector(`script[src*="sherpa-onnx-vad.js"]`);
      const existingAsrScript = document.querySelector(`script[src*="sherpa-onnx-asr.js"]`);
      const existingWasmScript = document.querySelector(`script[src*="sherpa-onnx-wasm-main-vad-asr.js"]`);

      if (existingVadScript || existingAsrScript || existingWasmScript) {
        console.log('Sherpa-ONNX: Scripts already loaded, waiting for initialization...');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const win = window as any;
        const checkInterval = setInterval(() => {
          if (win.createVad && win.OfflineRecognizer && win.CircularBuffer && window.Module) {
            clearInterval(checkInterval);
            try {
              this.initializeRecognizers();
              resolve();
            } catch (error) {
              reject(error);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (!this.recognizer) {
            reject(new Error('Timeout waiting for existing Sherpa scripts'));
          }
        }, 30000);
        return;
      }

      // Setup Module for Emscripten
      window.Module = window.Module || {} as SherpaModule;

      // Configure file locations
      window.Module.locateFile = (path: string) => {
        console.log(`Sherpa locateFile: ${path}`);
        return `${SHERPA_BASE_PATH}/${path}`;
      };

      // Track loading progress
      window.Module.setStatus = (status: string) => {
        console.log(`Sherpa status: ${status}`);

        // Parse download progress
        const downloadMatch = status.match(/Downloading data... \((\d+)\/(\d+)\)/);
        if (downloadMatch) {
          const downloaded = BigInt(downloadMatch[1]);
          const total = BigInt(downloadMatch[2]);
          const percent = total === 0n ? 0 : Number((downloaded * 100n) / total);
          this.config.onLoadProgress?.(percent, `Downloading model: ${percent}%`);
        } else if (status.includes('Running')) {
          this.config.onLoadProgress?.(90, 'Initializing recognizer...');
        } else if (status === '') {
          this.config.onLoadProgress?.(100, 'Ready');
        }
      };

      // Callback when WASM is ready
      window.Module.onRuntimeInitialized = () => {
        console.log('Sherpa-ONNX WASM runtime initialized');

        // Wait for Module._malloc to be available (WASM functions load async)
        const waitForWasm = () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mod = window.Module as any;
          if (typeof mod._malloc === 'function') {
            console.log('WASM functions ready, initializing recognizers...');
            try {
              this.initializeRecognizers();
              resolve();
            } catch (error) {
              console.error('Failed to initialize recognizers:', error);
              reject(error);
            }
          } else {
            console.log('Waiting for WASM functions...');
            setTimeout(waitForWasm, 100);
          }
        };
        waitForWasm();
      };

      // Load scripts in order matching the demo: ASR helper, VAD helper, then WASM
      // The order matters! ASR must come before VAD.
      this.loadScript(`${SHERPA_BASE_PATH}/sherpa-onnx-asr.js`)
        .then(() => {
          console.log('ASR helper loaded');
          return this.loadScript(`${SHERPA_BASE_PATH}/sherpa-onnx-vad.js`);
        })
        .then(() => {
          console.log('VAD helper loaded');
          // Expose the classes to window explicitly (they're defined as globals but not on window)
          return this.loadInlineScript(`
            if (typeof CircularBuffer !== 'undefined') window.CircularBuffer = CircularBuffer;
            if (typeof OfflineRecognizer !== 'undefined') window.OfflineRecognizer = OfflineRecognizer;
            if (typeof createVad !== 'undefined') window.createVad = createVad;
            console.log('Exposed to window:', {
              CircularBuffer: typeof window.CircularBuffer,
              OfflineRecognizer: typeof window.OfflineRecognizer,
              createVad: typeof window.createVad
            });
          `);
        })
        .then(() => {
          console.log('Globals exposed, loading WASM...');
          return this.loadScript(`${SHERPA_BASE_PATH}/sherpa-onnx-wasm-main-vad-asr.js`);
        })
        .then(() => {
          console.log('WASM script loaded, waiting for runtime initialization...');
        })
        .catch(reject);

      // Timeout after 120 seconds (model download can be slow)
      setTimeout(() => {
        if (!this.recognizer) {
          reject(new Error('Sherpa-ONNX initialization timeout'));
        }
      }, 120000);
    });
  }

  /**
   * Load a script dynamically
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Load an inline script
   */
  private loadInlineScript(code: string): Promise<void> {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.textContent = code;
      document.head.appendChild(script);
      resolve();
    });
  }

  /**
   * Check if a file exists in the WASM filesystem
   */
  private fileExists(filename: string): boolean {
    const Module = window.Module;
    if (!Module.lengthBytesUTF8 || !Module._malloc || !Module._free || !Module._SherpaOnnxFileExists) {
      return false;
    }

    const filenameLen = Module.lengthBytesUTF8(filename) + 1;
    const buffer = Module._malloc(filenameLen);
    Module.stringToUTF8!(filename, buffer, filenameLen);

    const exists = Module._SherpaOnnxFileExists(buffer);
    Module._free(buffer);

    return exists === 1;
  }

  /**
   * Initialize VAD and Recognizer after WASM is ready
   */
  private initializeRecognizers(): void {
    // Access globals from window (we explicitly attached them via inline script)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const win = window as any;

    const createVadFn = win.createVad;
    const CircularBufferClass = win.CircularBuffer;
    const OfflineRecognizerClass = win.OfflineRecognizer;

    console.log('Checking window globals:', {
      createVad: typeof createVadFn,
      CircularBuffer: typeof CircularBufferClass,
      OfflineRecognizer: typeof OfflineRecognizerClass,
    });

    if (!createVadFn || !CircularBufferClass || !OfflineRecognizerClass) {
      throw new Error('Sherpa-ONNX functions not available');
    }

    // Create VAD
    this.vad = createVadFn(window.Module);
    console.log('VAD created:', this.vad);

    // Create circular buffer for audio (30 seconds at 16kHz)
    this.buffer = new CircularBufferClass(30 * EXPECTED_SAMPLE_RATE, window.Module);
    console.log('CircularBuffer created');

    // Create offline recognizer - auto-detect model type from embedded files
    // This matches the logic in app-vad-asr.js initOfflineRecognizer()
    const config: OfflineRecognizerConfig = {
      modelConfig: {
        debug: 1,
        tokens: './tokens.txt',
      },
    };

    // Debug: Check what files exist
    const filesToCheck = [
      'sense-voice.onnx',
      'whisper-encoder.onnx',
      'transducer-encoder.onnx',
      'nemo-transducer-encoder.onnx',
      'paraformer.onnx',
      'telespeech.onnx',
      'moonshine-preprocessor.onnx',
      'dolphin.onnx',
      'zipformer-ctc.onnx',
      'encoder.onnx',
      'decoder.onnx',
      'joiner.onnx',
      'tokens.txt',
    ];
    console.log('Checking for model files in WASM data:');
    for (const file of filesToCheck) {
      console.log(`  ${file}: ${this.fileExists(file)}`);
    }

    // Auto-detect model type based on what files exist in the WASM data
    if (this.fileExists('sense-voice.onnx')) {
      console.log('Detected SenseVoice model');
      (config.modelConfig as Record<string, unknown>).senseVoice = {
        model: './sense-voice.onnx',
        useInverseTextNormalization: 1,
      };
    } else if (this.fileExists('whisper-encoder.onnx')) {
      console.log('Detected Whisper model');
      (config.modelConfig as Record<string, unknown>).whisper = {
        encoder: './whisper-encoder.onnx',
        decoder: './whisper-decoder.onnx',
      };
    } else if (this.fileExists('transducer-encoder.onnx')) {
      console.log('Detected Transducer model');
      config.modelConfig.transducer = {
        encoder: './transducer-encoder.onnx',
        decoder: './transducer-decoder.onnx',
        joiner: './transducer-joiner.onnx',
      };
      (config.modelConfig as Record<string, unknown>).modelType = 'transducer';
    } else if (this.fileExists('nemo-transducer-encoder.onnx')) {
      console.log('Detected NeMo Transducer model');
      config.modelConfig.transducer = {
        encoder: './nemo-transducer-encoder.onnx',
        decoder: './nemo-transducer-decoder.onnx',
        joiner: './nemo-transducer-joiner.onnx',
      };
      (config.modelConfig as Record<string, unknown>).modelType = 'nemo_transducer';
    } else if (this.fileExists('paraformer.onnx')) {
      console.log('Detected Paraformer model');
      (config.modelConfig as Record<string, unknown>).paraformer = {
        model: './paraformer.onnx',
      };
    } else if (this.fileExists('telespeech.onnx')) {
      console.log('Detected TeleSpeech model');
      (config.modelConfig as Record<string, unknown>).teleSpeechCtc = './telespeech.onnx';
    } else if (this.fileExists('moonshine-preprocessor.onnx')) {
      console.log('Detected Moonshine model');
      (config.modelConfig as Record<string, unknown>).moonshine = {
        preprocessor: './moonshine-preprocessor.onnx',
        encoder: './moonshine-encoder.onnx',
        uncachedDecoder: './moonshine-uncached-decoder.onnx',
        cachedDecoder: './moonshine-cached-decoder.onnx',
      };
    } else if (this.fileExists('dolphin.onnx')) {
      console.log('Detected Dolphin model');
      (config.modelConfig as Record<string, unknown>).dolphin = { model: './dolphin.onnx' };
    } else if (this.fileExists('zipformer-ctc.onnx')) {
      console.log('Detected Zipformer CTC model');
      (config.modelConfig as Record<string, unknown>).zipformerCtc = { model: './zipformer-ctc.onnx' };
    } else {
      console.warn('No recognized ASR model found in WASM data');
      throw new Error('No ASR model found in WASM data. Please check the WASM files.');
    }

    this.recognizer = new OfflineRecognizerClass(config, window.Module);
    console.log('OfflineRecognizer created');
  }

  /**
   * Start listening for wake word
   */
  async startListening(): Promise<void> {
    console.log('SherpaVoiceService.startListening called');
    this.explicitlyStopped = false;

    // Initialize if needed
    if (!this.isInitialized) {
      const success = await this.initialize();
      if (!success) {
        this.config.onError?.(new Error('Failed to initialize offline voice'));
        return;
      }
    }

    if (!this.recognizer || !this.vad || !this.buffer) {
      this.config.onError?.(new Error('Recognizer not available'));
      return;
    }

    try {
      // Get microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      // Create audio context
      this.audioContext = new AudioContext({ sampleRate: EXPECTED_SAMPLE_RATE });
      this.recordSampleRate = this.audioContext.sampleRate;
      console.log('Audio context sample rate:', this.recordSampleRate);

      // Create audio processing pipeline
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);

      // Create processor for audio chunks
      const bufferSize = 4096;
      this.recorder = this.audioContext.createScriptProcessor(bufferSize, 1, 2);

      // Reset state
      this.speechDetected = false;
      this.currentTranscript = '';

      // Process audio
      this.recorder.onaudioprocess = (e) => {
        if (this.explicitlyStopped) return;

        const inputSamples = e.inputBuffer.getChannelData(0);
        let samples: Float32Array;

        // Downsample if needed
        if (this.recordSampleRate !== EXPECTED_SAMPLE_RATE) {
          samples = this.downsampleBuffer(new Float32Array(inputSamples), EXPECTED_SAMPLE_RATE);
        } else {
          samples = new Float32Array(inputSamples);
        }

        this.processAudioWithVad(samples);
      };

      // Connect audio pipeline
      source.connect(this.recorder);
      this.recorder.connect(this.audioContext.destination);

      this.setState('listening-wake');
      console.log('Sherpa-ONNX VAD+ASR listening started');

    } catch (error) {
      console.error('Failed to start Sherpa listening:', error);
      this.config.onError?.(error instanceof Error ? error : new Error('Microphone access denied'));
    }
  }

  /**
   * Process audio samples through VAD and ASR
   */
  private processAudioWithVad(samples: Float32Array): void {
    if (!this.vad || !this.buffer || !this.recognizer) return;

    // Push samples to circular buffer
    this.buffer.push(samples);

    // Process through VAD
    while (this.buffer.size() > this.vad.config.sileroVad.windowSize) {
      const windowSamples = this.buffer.get(
        this.buffer.head(),
        this.vad.config.sileroVad.windowSize
      );
      this.vad.acceptWaveform(windowSamples);
      this.buffer.pop(this.vad.config.sileroVad.windowSize);

      // Check for speech detection
      if (this.vad.isDetected() && !this.speechDetected) {
        this.speechDetected = true;
        console.log('Speech detected');
      }

      // Check for speech end
      if (!this.vad.isDetected() && this.speechDetected) {
        this.speechDetected = false;
      }

      // Process completed speech segments
      while (!this.vad.isEmpty()) {
        const segment = this.vad.front();
        this.vad.pop();

        const duration = segment.samples.length / EXPECTED_SAMPLE_RATE;
        console.log(`Speech segment: ${duration.toFixed(2)}s`);

        // Skip very short segments (likely noise)
        if (duration < 0.3) {
          console.log('Skipping short segment');
          continue;
        }

        // Process through ASR
        const stream = this.recognizer.createStream();
        stream.acceptWaveform(EXPECTED_SAMPLE_RATE, segment.samples);
        this.recognizer.decode(stream);
        const result = this.recognizer.getResult(stream);
        stream.free();

        const text = result.text.trim();
        console.log('Recognition result:', text);

        if (text.length > 0) {
          this.currentTranscript = text;
          this.config.onTranscript?.(text, true);
          this.processTranscript(text);
        }
      }
    }
  }

  /**
   * Process transcribed text for wake word and commands
   */
  private processTranscript(text: string): void {
    // Check for wake word
    const hasWakeWord = WAKE_WORD_PATTERNS.some(pattern => pattern.test(text));

    if (!this.isListeningForCommand && hasWakeWord) {
      console.log('Wake word detected:', text);
      this.isListeningForCommand = true;
      this.setState('listening-command');
      this.startFollowUpWindow();

      // Check if there's a command after the wake word
      let commandPart = text;
      WAKE_WORD_PATTERNS.forEach(pattern => {
        commandPart = commandPart.replace(pattern, '').trim();
      });

      if (commandPart.length > 0) {
        this.setState('processing');
        const intent = this.parseIntent(text);
        this.config.onIntent?.(intent);
        this.startFollowUpWindow();
        this.setState('listening-command');
      }
    } else if (this.isListeningForCommand) {
      // Process as command
      this.setState('processing');
      const intent = this.parseIntent(text);
      this.config.onIntent?.(intent);
      this.startFollowUpWindow();
      this.setState('listening-command');
    }
  }

  /**
   * Downsample audio buffer to target sample rate
   */
  private downsampleBuffer(buffer: Float32Array, exportSampleRate: number): Float32Array {
    if (exportSampleRate === this.recordSampleRate) {
      return buffer;
    }

    const sampleRateRatio = this.recordSampleRate / exportSampleRate;
    const newLength = Math.round(buffer.length / sampleRateRatio);
    const result = new Float32Array(newLength);

    let offsetResult = 0;
    let offsetBuffer = 0;

    while (offsetResult < result.length) {
      const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
      let accum = 0;
      let count = 0;

      for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
        accum += buffer[i];
        count++;
      }

      result[offsetResult] = count > 0 ? accum / count : 0;
      offsetResult++;
      offsetBuffer = nextOffsetBuffer;
    }

    return result;
  }

  /**
   * Stop listening
   */
  stopListening(): void {
    console.log('SherpaVoiceService.stopListening called');
    this.explicitlyStopped = true;
    this.isListeningForCommand = false;
    this.clearFollowUpWindow();

    // Disconnect audio pipeline
    if (this.recorder) {
      this.recorder.disconnect();
      this.recorder = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Reset VAD and buffer
    if (this.vad) {
      this.vad.reset();
    }
    if (this.buffer) {
      this.buffer.reset();
    }

    this.currentTranscript = '';
    this.speechDetected = false;

    this.setState('idle');
  }

  /**
   * Activate push-to-talk mode
   */
  activatePushToTalk(): void {
    console.log('SherpaVoiceService.activatePushToTalk called');
    this.explicitlyStopped = false;
    this.isListeningForCommand = true;
    this.setState('listening-command');

    // Start listening if not already
    if (!this.audioContext) {
      this.startListening();
    }
  }

  /**
   * Start/reset follow-up window timer
   */
  private startFollowUpWindow(): void {
    this.clearFollowUpWindow();

    const duration = this.config.followUpDurationMs || FOLLOW_UP_WINDOW_MS;

    this.followUpTimeout = setTimeout(() => {
      this.isListeningForCommand = false;
      this.setState('listening-wake');
      this.followUpTimeout = null;
    }, duration);
  }

  /**
   * Clear follow-up window timer
   */
  private clearFollowUpWindow(): void {
    if (this.followUpTimeout) {
      clearTimeout(this.followUpTimeout);
      this.followUpTimeout = null;
    }
  }

  /**
   * Update follow-up duration
   */
  setFollowUpDuration(durationMs: number): void {
    this.config.followUpDurationMs = durationMs;
  }

  /**
   * Update state and notify listeners
   */
  private setState(state: VoiceState): void {
    this.state = state;
    this.config.onStateChange?.(state);
  }

  /**
   * Get current state
   */
  getState(): VoiceState {
    return this.state;
  }

  /**
   * Check if Sherpa-ONNX is initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.recognizer !== null;
  }

  /**
   * Parse natural language into structured intent
   */
  parseIntent(transcript: string): VoiceIntent {
    const lowerTranscript = transcript.toLowerCase();

    // Remove wake word from transcript
    let cleanTranscript = lowerTranscript;
    WAKE_WORD_PATTERNS.forEach(pattern => {
      cleanTranscript = cleanTranscript.replace(pattern, '').trim();
    });

    // Detect intent type
    let type: VoiceIntent['type'] = 'search';
    let imageNumber: number | undefined;

    // Image selection
    const numberWords: Record<string, number> = {
      'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
      'six': 6, 'seven': 7, 'eight': 8, 'nine': 9,
      '1': 1, '2': 2, '3': 3, '4': 4, '5': 5,
      '6': 6, '7': 7, '8': 8, '9': 9,
    };

    const selectPatterns = [
      /(?:image|number|select|look at|open|show)\s+(\w+)/i,
      /^(\w+)$/i,
    ];

    for (const pattern of selectPatterns) {
      const match = cleanTranscript.match(pattern);
      if (match) {
        const numWord = match[1].toLowerCase();
        if (numberWords[numWord]) {
          imageNumber = numberWords[numWord];
          type = 'select';
          break;
        }
      }
    }

    // Other intent types
    if (cleanTranscript.includes('compare') || cleanTranscript.includes('before and after')) {
      type = 'compare';
    } else if (cleanTranscript.includes('similar') || cleanTranscript.includes('more like')) {
      type = 'similar';
    } else if (cleanTranscript.includes('back') || cleanTranscript.includes('exit') || cleanTranscript.includes('home')) {
      type = 'navigate';
    } else if (cleanTranscript.includes('timeline')) {
      type = 'timeline';
    }

    // Extract component and condition
    const component = this.extractComponent(cleanTranscript);
    const condition = this.extractCondition(cleanTranscript);

    // Calculate confidence
    let confidence = 0.5;
    if (component) confidence += 0.2;
    if (condition) confidence += 0.2;
    if (type !== 'search') confidence += 0.1;

    return {
      type,
      query: cleanTranscript,
      component,
      condition,
      confidence,
      rawTranscript: transcript,
      imageNumber,
    };
  }

  /**
   * Check if transcript contains keyword with word boundary awareness
   * Handles: plurals (engines→engine), gerunds (steering), word boundaries, case insensitivity
   */
  private matchesKeyword(transcript: string, keyword: string): boolean {
    const normalized = transcript.toLowerCase();
    const keywordLower = keyword.toLowerCase();

    // For multi-word keywords, use simple includes
    if (keywordLower.includes(' ')) {
      return normalized.includes(keywordLower);
    }

    // For single words, use word boundaries to avoid false positives
    const patterns = [
      new RegExp(`\\b${keywordLower}\\b`),           // exact match
      new RegExp(`\\b${keywordLower}s\\b`),          // plural (engines)
      new RegExp(`\\b${keywordLower}ing\\b`),        // gerund (steering)
    ];

    return patterns.some(pattern => pattern.test(normalized));
  }

  /**
   * Extract component category from transcript
   */
  private extractComponent(transcript: string): ComponentCategory | undefined {
    const componentKeywords: Record<ComponentCategory, string[]> = {
      engine: ['engine', 'motor', 'block', 'heads', 'intake', 'exhaust manifold', 'v8', 'v6', 'inline', 'powerplant'],
      suspension: ['suspension', 'shock', 'spring', 'control arm', 'sway bar', 'strut', 'a-arm', 'leaf spring', 'coilover'],
      interior: ['interior', 'seat', 'dashboard', 'dash', 'carpet', 'headliner', 'door panel', 'console', 'upholstery', 'trim panel'],
      body: ['body', 'fender', 'hood', 'trunk', 'quarter panel', 'door', 'rocker', 'floor pan', 'firewall', 'cowl'],
      electrical: ['electrical', 'wiring', 'harness', 'gauge', 'light', 'alternator', 'starter', 'battery', 'fuse'],
      drivetrain: ['drivetrain', 'drive train', 'drive-train', 'transmission', 'trans', 'differential', 'diff', 'driveshaft', 'drive shaft', 'clutch', 'axle', 'cv joint', 'u joint', 'u-joint', 'rear end'],
      brakes: ['brake', 'brakes', 'break', 'breaks', 'rotor', 'caliper', 'brake line', 'master cylinder', 'drum', 'pad', 'shoe'],
      exhaust: ['exhaust', 'header', 'muffler', 'pipe', 'catalytic', 'cat', 'manifold', 'tailpipe'],
      cooling: ['cooling', 'radiator', 'water pump', 'thermostat', 'hose', 'fan', 'coolant', 'overflow'],
      fuel: ['fuel', 'gas tank', 'fuel line', 'carburetor', 'carb', 'fuel injection', 'injector', 'fuel pump'],
      steering: ['steering', 'steering wheel', 'steering column', 'rack', 'power steering', 'tie rod', 'pitman arm'],
      wheels: ['wheel', 'tire', 'rim', 'hubcap', 'wheel bearing', 'lug', 'hub'],
      trim: ['trim', 'chrome', 'molding', 'emblem', 'badge', 'bezel', 'garnish'],
      glass: ['glass', 'windshield', 'window', 'rear glass', 'side glass', 'weatherstrip'],
      other: [],
    };

    for (const [category, keywords] of Object.entries(componentKeywords)) {
      if (keywords.some((keyword) => this.matchesKeyword(transcript, keyword))) {
        return category as ComponentCategory;
      }
    }

    return undefined;
  }

  /**
   * Extract condition state from transcript
   */
  private extractCondition(transcript: string): ConditionState | undefined {
    const conditionKeywords: Record<ConditionState, string[]> = {
      'as-received': [
        'as received', 'as-received', 'when we got it', 'original', 'before',
        'how it came in', 'arrival', 'when it arrived', 'starting point',
        'day one', 'initial', 'untouched'
      ],
      disassembly: [
        'disassembly', 'taking apart', 'teardown', 'tear down', 'disassembled',
        'pulled apart', 'stripped down', 'dismantled'
      ],
      stripped: [
        'stripped', 'bare', 'bare metal', 'media blasted', 'sandblasted',
        'down to metal', 'clean metal'
      ],
      cleaned: [
        'cleaned', 'degreased', 'washed', 'prepped', 'prepared',
        'ready for paint', 'ready for primer'
      ],
      repaired: [
        'repaired', 'fixed', 'welded', 'patched', 'fabricated',
        'metal work', 'bodywork', 'rust repair'
      ],
      primed: [
        'primed', 'primer', 'in primer', 'epoxy', 'sealed',
        'base coat', 'prepped for paint'
      ],
      painted: [
        'painted', 'sprayed', 'color', 'base clear', 'finished paint',
        'wet sanded', 'buffed'
      ],
      restored: [
        'restored', 'after', 'after restoration', 'completed', 'done',
        'finished', 'final', 'now', 'current', 'today'
      ],
      installed: [
        'installed', 'put back', 'reassembled', 'mounted', 'fitted',
        'back together', 'reinstalled', 'bolted up'
      ],
      completed: [
        'completed', 'done', 'finished', 'final result', 'end result',
        'all done', 'wrapped up'
      ],
    };

    // Priority order: more specific states checked first
    // Generic terms like "after" and "finished" are in 'restored' and 'completed'
    // which should only match if no more specific state matches
    const priorityOrder: ConditionState[] = [
      'as-received',
      'disassembly',
      'stripped',
      'cleaned',
      'repaired',
      'primed',
      'painted',
      'installed',
      'restored',
      'completed',
    ];

    for (const condition of priorityOrder) {
      const keywords = conditionKeywords[condition];
      if (keywords.some((keyword) => this.matchesKeyword(transcript, keyword))) {
        return condition;
      }
    }

    return undefined;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.stopListening();

    if (this.vad) {
      this.vad.free();
      this.vad = null;
    }

    if (this.buffer) {
      this.buffer.free();
      this.buffer = null;
    }

    if (this.recognizer) {
      this.recognizer.free();
      this.recognizer = null;
    }

    this.isInitialized = false;
  }
}

/**
 * Singleton instance
 */
let sherpaVoiceServiceInstance: SherpaVoiceService | null = null;

export function getSherpaVoiceService(config?: SherpaVoiceConfig): SherpaVoiceService {
  if (!sherpaVoiceServiceInstance) {
    sherpaVoiceServiceInstance = new SherpaVoiceService(config);
  }
  return sherpaVoiceServiceInstance;
}

/**
 * Reset the singleton instance (useful for testing or reconfiguration)
 */
export function resetSherpaVoiceService(): void {
  if (sherpaVoiceServiceInstance) {
    sherpaVoiceServiceInstance.destroy();
    sherpaVoiceServiceInstance = null;
  }
}
